/** @jsx jsx */
import { gql, useQuery } from '@apollo/client';
import { Col, Container } from '@bottlebooks/gatsby-theme-base';
import BrandBanner, {
  BrandBannerFragment,
} from '@bottlebooks/gatsby-theme-event/src/components/Brand/BrandBanner';
import ContentRow from '@bottlebooks/gatsby-theme-event/src/components/ContentRow';
import EventProductPageHeader, {
  EventProductPageHeaderFragment,
} from '@bottlebooks/gatsby-theme-event/src/components/EventProductPage/EventProductPageHeader';
import PreviewLayout from '@bottlebooks/gatsby-theme-event/src/components/Layout/PreviewLayout';
import ProductActions from '@bottlebooks/gatsby-theme-event/src/components/Product/ProductActions';
import { ProductLabelFragment } from '@bottlebooks/gatsby-theme-event/src/components/Product/ProductLabel';
import ProductNavigation from '@bottlebooks/gatsby-theme-event/src/components/ProductPage/ProductNavigation';
import ProductSections from '@bottlebooks/gatsby-theme-event/src/components/ProductPage/ProductSections';
import useLink from '@bottlebooks/gatsby-theme-event/src/useLink';
import { jsx } from 'theme-ui';
import getWfsPrice from '../../../gatsby-theme-event-pages/templates/getWfsPrice';

export default function ProductPreviewPage({
  productId,
  companyId,
  collectionId,
  locale,
}) {
  const link = useLink();
  const { data, loading, error } = useQuery(clientQuery, {
    variables: {
      collectionId,
      locale,
      companyId,
      productId,
    },
  });

  if (loading) return <PreviewLayout locale={locale}>Loading…</PreviewLayout>;
  if (error) {
    console.error(error);
    return (
      <PreviewLayout locale={locale}>
        We're sorry, but there was an error.
      </PreviewLayout>
    );
  }
  const { exhibitor, producer, product, previous, next } = afterQuery(data);
  const standPath = exhibitor
    ? link.exhibitor(exhibitor)
    : link.producer(producer);

  return (
    <PreviewLayout locale={locale}>
      {exhibitor ? (
        <BrandBanner {...exhibitor} />
      ) : (
        <BrandBanner {...producer} />
      )}
      <Container>
        <EventProductPageHeader
          product={product}
          overlapImage
          previous={previous}
          next={next}
          standPath={standPath}
        />
      </Container>

      <Container>
        <ContentRow>
          <Col sx={{ paddingY: 3 }}>
            <ProductActions product={product} />
          </Col>
        </ContentRow>
      </Container>

      <ProductSections
        exhibitor={exhibitor}
        product={product}
        eventId={collectionId}
      />
    </PreviewLayout>
  );
}

export const clientQuery = gql`
  query ProductPreviewPage(
    $collectionId: ID!
    $productId: ID!
    $companyId: ID!
    $locale: ContentLocale
  ) {
    event(eventId: $collectionId, locale: $locale) {
      eventId # used for the cache.
      exhibitor(exhibitorId: $companyId) {
        exhibitorId # used for the cache.
        slug(useIdOnly: true)
        name
        ...BrandBanner
        products {
          nodes {
            productId
            ...ProductNavigation
          }
        }
      }
      registeredProducts(
        filter: { productId: { eq: $productId }, partnerId: { eq: $companyId } }
      ) {
        nodes {
          ...ProductSections
          ...ProductActions_RegisteredProduct
          ...bb_ProductLabel_RegisteredProduct
          ...custom_ProductSections
          registration {
            exhibitorType: customFieldValue(key: "exhibitorType") {
              __typename
              ... on SelectFieldValue {
                key
              }
            }
          }
          wfsPrice: customFieldValue(key: "wfsPrice") {
            ... on TextFieldValue {
              text
            }
          }
          product {
            productId # used for the cache.
            producer {
              producerId # used for the cache.
              slug(useIdOnly: true)
              name
              ...BrandBanner
              ...useLink_Producer
            }
            ...EventProductPageHeader
            ...ProductActions
            ... on ExtendedProduct {
              exhibitor {
                exhibitorId # needed for the apolloClient cache.
                ...useLink_Exhibitor
              }
            }
          }
        }
      }
    }
  }
  ${ProductNavigation.fragment}
  ${BrandBannerFragment}
  ${EventProductPageHeaderFragment}
  ${ProductActions.fragment}
  ${ProductSections.fragment}
  ${useLink.producerFragment}
  ${useLink.exhibitorFragment}
  ${ProductLabelFragment}
`;

function afterQuery({ event }) {
  const product = event?.registeredProducts?.nodes?.[0] || {};
  const exhibitor = event?.exhibitor;
  const { producer } = product || {};
  const products = exhibitor?.products?.nodes;
  const currentIndex = products?.findIndex(
    (p) => p?.productId === product.product?.productId
  );
  const previous = currentIndex > 0 ? products?.[currentIndex - 1] : null;
  const next = currentIndex > -1 ? products?.[currentIndex + 1] : null;
  const exhibitorType = product?.registration?.exhibitorType?.key;
  const price = product?.wfsPrice?.text;
  return {
    exhibitor,
    producer,
    product: {
      ...product,
      ...product.product,
      ...getWfsPrice({ exhibitorType, price }),
    },
    previous,
    next,
  };
}
